import { useMemo, useEffect } from 'react';
import { Pagination } from '@heroui/react';
import { ContentWrapper } from '@/widgets/ContentWrapper';
import { CustomBreadcrumbs, IBreadcrumbItem } from '@/widgets/CustomBreadcrumbs';
import {
  useGetTeacherAssignmentsDetailsQuery,
  useGetTeacherAssignmentsSummaryQuery,
  TeacherAssessmentType,
} from '@/entities/TeacherAssignment';
import { SelectField, CheckboxField, CustomInput, SelectOption } from '@/shared/ui/Fields';
import SearchIcon from '@/shared/assets/icons/search.svg';
import { Svg } from '@/shared/ui/Svg';
import { TeacherAssignmentSummary } from '../components/TeacherAssignmentSummary';
import { TeacherAssignmentItem } from '../components/TeacherAssignmentItem';
import { useTeacherAssignmentFilters } from '../utils/hooks/useTeacherAssignmentFilters';
import { useFilterOptions } from '../utils/hooks/useFilterOptions';
import { getTeacherAssignmentsBreadcrumbs } from '../utils/getBreadcrumbs';

const COURSE_TYPE_OPTIONS: SelectOption[] = [
  { value: 'masters-degree', label: "Master's Degree" },
  { value: 'postgraduate', label: 'Postgraduate' },
  { value: 'bachelors-degree', label: "Bachelor's Degree" },
  { value: 'short', label: 'Short Course' },
];

export const TeacherAssignmentsView = () => {
  const { filters, currentPage, setCurrentPage, updateFilter, resetDependentFilters } =
    useTeacherAssignmentFilters();
  const {
    courseOptions,
    intakeOptions,
    termOptions,
    groupOptions,
    selectedCourse,
    isLoading,
    coursesData,
  } = useFilterOptions(filters.courseType, filters.courseId);

  const pageSize = 6;

  // Если courseId есть в URL, но courseType отсутствует, определяем его из загруженных данных
  useEffect(() => {
    if (filters.courseId && !filters.courseType && coursesData?.courses) {
      const course = coursesData.courses.find((course) => String(course.id) === filters.courseId);
      if (course?.type) {
        updateFilter('courseType', course.type);
      }
    }
  }, [filters.courseId, filters.courseType, coursesData, updateFilter]);

  const breadcrumbs = useMemo<IBreadcrumbItem[]>(
    () =>
      getTeacherAssignmentsBreadcrumbs({
        courseId: filters.courseId,
        intakeId: filters.intakeId,
        selectedCourse,
      }),
    [filters.courseId, filters.intakeId, selectedCourse],
  );

  const assessmentType: TeacherAssessmentType = useMemo(() => {
    if (filters.isFormative && filters.isSummative) return 'all';
    if (filters.isFormative) return 'formative';
    if (filters.isSummative) return 'summative';
    return 'all';
  }, [filters.isFormative, filters.isSummative]);

  const studentState = useMemo(() => {
    if (filters.isActive && !filters.isCompleted) return 'active' as const;
    if (filters.isCompleted && !filters.isActive) return 'graduated' as const;
    return undefined;
  }, [filters.isActive, filters.isCompleted]);

  const baseFilters = useMemo(() => {
    if (!filters.courseId) return null;
    return {
      courseId: Number(filters.courseId),
      intakeId: filters.intakeId ? Number(filters.intakeId) : undefined,
      termNumber: filters.termNumber ? Number(filters.termNumber) : undefined,
      groupNumber: filters.groupNumber ? Number(filters.groupNumber) : undefined,
      studentState,
      assessmentType,
      search: filters.search || undefined,
    };
  }, [
    filters.courseId,
    filters.intakeId,
    filters.termNumber,
    filters.groupNumber,
    filters.search,
    studentState,
    assessmentType,
  ]);

  const summaryFilters = baseFilters;

  const detailsFilters = useMemo(() => {
    if (!baseFilters) return null;
    return {
      ...baseFilters,
      page: currentPage,
      pageSize,
    };
  }, [baseFilters, currentPage, pageSize]);

  const { data: summaryData, isLoading: isSummaryLoading } = useGetTeacherAssignmentsSummaryQuery(
    summaryFilters!,
    { skip: !summaryFilters },
  );

  const { data: assignmentsData, isLoading: isAssignmentsLoading } =
    useGetTeacherAssignmentsDetailsQuery(detailsFilters!, { skip: !detailsFilters });

  const handleCourseTypeChange = (value: string) => {
    updateFilter('courseType', value);
    resetDependentFilters('courseType');
  };

  const handleCourseChange = (value: string) => {
    updateFilter('courseId', value);
    resetDependentFilters('courseId');
  };

  const handleIntakeChange = (value: string) => {
    updateFilter('intakeId', value);
    resetDependentFilters('intakeId');
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-6 bg-white rounded-[24px] p-6">
        {breadcrumbs.length > 1 ? (
          <CustomBreadcrumbs items={breadcrumbs} />
        ) : (
          <p className="text-[24px] font-medium leading-[32px]">Assignments</p>
        )}
        <p>All assignments across your courses</p>
        <div className="max-w-[400px]">
          <CustomInput
            startContent={<Svg Icon={SearchIcon} className="w-5 h-5" />}
            placeholder="Search"
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
          />
        </div>

        <ContentWrapper>
          <div className="grid grid-cols-2 gap-4">
            <div className="flex flex-col gap-4">
              <SelectField
                name="courseType"
                label="Type"
                placeholder="Select type"
                options={COURSE_TYPE_OPTIONS}
                value={filters.courseType}
                onChange={handleCourseTypeChange}
              />
              <SelectField
                name="course"
                label="Course"
                placeholder="Select course"
                options={courseOptions}
                value={filters.courseId}
                onChange={handleCourseChange}
                disabled={!filters.courseType || isLoading}
              />
              <SelectField
                name="intake"
                label="Intake"
                placeholder="Select intake"
                options={intakeOptions}
                value={filters.intakeId}
                onChange={handleIntakeChange}
                disabled={!filters.courseId || isLoading}
              />
            </div>
            <div className="flex flex-col gap-4">
              <SelectField
                name="term"
                label="Term"
                placeholder="Select term number"
                options={termOptions}
                value={filters.termNumber}
                onChange={(value) => updateFilter('termNumber', value)}
                disabled={!filters.intakeId || isLoading}
              />
              <SelectField
                name="group"
                label="Group"
                placeholder="Select group number"
                options={groupOptions}
                value={filters.groupNumber}
                onChange={(value) => updateFilter('groupNumber', value)}
                disabled={!filters.intakeId || isLoading}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-x-6 gap-y-2 mt-4 max-w-[300px]">
            <CheckboxField
              name="formative"
              label="Formative"
              checked={filters.isFormative}
              onChange={(value) => updateFilter('isFormative', value)}
            />
            <CheckboxField
              name="summative"
              label="Summative"
              checked={filters.isSummative}
              onChange={(value) => updateFilter('isSummative', value)}
            />
            <CheckboxField
              name="active"
              label="Active"
              checked={filters.isActive}
              onChange={(value) => updateFilter('isActive', value)}
            />
            <CheckboxField
              name="completed"
              label="Completed"
              checked={filters.isCompleted}
              onChange={(value) => updateFilter('isCompleted', value)}
            />
          </div>
        </ContentWrapper>

        {/* Summary & Assignments */}
        {filters.courseId && !isLoading && (
          <ContentWrapper isLoading={isSummaryLoading || isAssignmentsLoading}>
            {summaryData && <TeacherAssignmentSummary {...summaryData} />}

            {assignmentsData && assignmentsData.items.length > 0 && (
              <>
                <ul className="flex flex-col gap-4 mt-6">
                  {assignmentsData.items.map((item) => (
                    <TeacherAssignmentItem
                      key={`${item.group.id}-${item.module.intakeModuleId}-${item.lesson.id}-${item.lesson.type}`}
                      item={item}
                    />
                  ))}
                </ul>
              </>
            )}
          </ContentWrapper>
        )}
      </div>
      {assignmentsData && assignmentsData.pagination.pageCount > 1 ? (
        <div className="flex justify-center">
          <Pagination
            total={assignmentsData.pagination.pageCount}
            page={currentPage}
            onChange={setCurrentPage}
            showControls
          />
        </div>
      ) : null}
    </div>
  );
};
